import { useAuthUtils } from '@hakimo-ui/hakimo/util';
import { useEffect, useRef } from 'react';
import { VideoRTC } from './video-rtc';

if (!customElements.get('go2rtc-player')) {
  customElements.define('go2rtc-player', VideoRTC);
}

interface Props {
  livestreamUrl: string;
}

function buildWsUrl(livestreamUrl: string, accessToken: string): string | null {
  const parsed = new URL(livestreamUrl);
  const streamName = parsed.searchParams.get('src');
  if (!streamName) return null;

  const isLocalDev = window.location.hostname === 'localhost';
  const tokenParam = accessToken
    ? `&access_token=${encodeURIComponent(accessToken)}`
    : '';

  if (isLocalDev) {
    // Relative path — video-rtc.js will prepend current origin with ws://
    return `/go2rtc-proxy/${parsed.host}/api/ws?src=${encodeURIComponent(
      streamName
    )}${tokenParam}`;
  }

  // Absolute https URL — video-rtc.js auto-converts http(s) → ws(s)
  return `https://${parsed.host}/api/ws?src=${encodeURIComponent(
    streamName
  )}${tokenParam}`;
}

export function Go2RtcNativePlayer(props: Props) {
  const { livestreamUrl } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const { getAccessToken } = useAuthUtils();

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let player: VideoRTC | null = null;
    let cancelled = false;

    async function init(el: HTMLDivElement) {
      let accessToken = '';
      try {
        accessToken = await getAccessToken();
      } catch {
        // proceed without token
      }

      if (cancelled) return;

      const wsUrl = buildWsUrl(livestreamUrl, accessToken);
      if (!wsUrl) return;

      player = document.createElement('go2rtc-player') as VideoRTC;
      player.style.display = 'block';
      player.style.width = '100%';
      player.style.height = '100%';
      el.appendChild(player);

      player.mode = 'webrtc,mse,hls,mjpeg';
      player.src = wsUrl;
    }

    init(container);

    return () => {
      cancelled = true;
      if (player) {
        player.ondisconnect();
        if (container.contains(player)) {
          container.removeChild(player);
        }
      }
    };
  }, [livestreamUrl, getAccessToken]);

  return (
    <div
      ref={containerRef}
      className="bg-ondark-bg-2 relative h-full w-full overflow-hidden"
    />
  );
}
